import { useCallback, useContext, useEffect, useState } from "react"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//GraphQL
import { GET_ORGANIZATION_PROGRAMS } from "../queries"

//Formik
import { useField } from "formik"
import FormikMultiSelectInput from "../../../helpers/formikInputs/FormikMultiSelectInput"
import { LogError } from "../../../helpers/logger"
import { useLazyQuery } from "@apollo/client"

export default function HousePrograms({
  isSubmitting,
  organizations,
  houseOrganizations,
  isLoadingPrograms,
  onSetIsLoadingPrograms,
}) {
  //Retrieve relevant context values
  const userCtx = useContext(UserContext)

  const [field, , helper] = useField("programs")

  const [availablePrograms, setAvailablePrograms] = useState([])
  const [programOptions, setProgramOptions] = useState([])

  const [getProgramsByOrganization] = useLazyQuery(GET_ORGANIZATION_PROGRAMS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: "",
    },
  })

  ///Ths function retrieves an organization's program list and adds it to the available program list.
  const getOrganizationProgramList = useCallback(
    async (organization_id) => {
      try {
        const organization = organizations.filter(
          (org) => org.organization_id === organization_id
        )

        if (organization.length > 0) {
          const result = await getProgramsByOrganization({
            variables: {
              staff_id: userCtx.session.user.staff_id,
              organization_id: organization_id,
            },
          })

          if (result.data.programsByOrganization.length > 0) {
            const fixedResult = result.data.programsByOrganization.map(
              (program) => {
                return {
                  organization_name: program.organization_name,
                  program_id: program.program_id,
                  program_id_name: program.program_id_name,
                  organization_id: organization_id,
                }
              }
            )

            return {
              organization_name: organization[0].organization_name,
              organization_id: organization_id,
              programs: fixedResult,
            }
          }
        } else {
          throw new Error("Organization not found")
        }
      } catch (error) {
        LogError("Unable to retrieve organization programs", error)
      }
    },
    [getProgramsByOrganization, organizations, userCtx.session.user.staff_id]
  )

  const updateProgramListing = useCallback(async () => {
    try {
      onSetIsLoadingPrograms(true)

      let newAvailablePrograms = []
      let newProgramOptions = []
      const hasRemovedOrgs = programOptions.some(
        (option) =>
          !houseOrganizations.some(
            (organization) => organization === option.organization_id
          )
      )

      for (const organizationID of houseOrganizations) {
        //Find available programs for this organization
        const availableProgram = availablePrograms.filter(
          (availableProgram) =>
            availableProgram.organization_id === organizationID
        )

        if (availableProgram.length === 0) {
          //If the organization is not in the available programs, we will need to retrieve it
          const newAvailableProgram = await getOrganizationProgramList(
            organizationID
          )

          if (!!newAvailableProgram) {
            newAvailablePrograms.push(newAvailableProgram)
            newProgramOptions.push(newAvailableProgram)
          }
        } else if (
          !programOptions.some(
            (programOption) => programOption.organization_id === organizationID
          )
        ) {
          //If the programs are available for the organization, but not an option, add as an option
          newProgramOptions.push(availableProgram[0])
        }
      }

      if (newAvailablePrograms.length > 0) {
        setAvailablePrograms([...availablePrograms, ...newAvailablePrograms])
      }

      if (newProgramOptions.length > 0) {
        //Add new entries and remove options that are no longer in houseORganizations.
        const newProgramOptionList = [...programOptions, ...newProgramOptions]

        const cleanList = newProgramOptionList.filter((option) =>
          houseOrganizations.some((org) => org === option.organization_id)
        )

        if (cleanList.length !== newProgramOptionList.length) {
          const cleanValues = field.value.filter((program) =>
            houseOrganizations.some((org) => org === program.organization_id)
          )
          helper.setValue(cleanValues)
        }

        setProgramOptions(cleanList)
      } else if (hasRemovedOrgs) {
        //Remove entries that have been removed from houseOrganizations
        const newProgramOptionList = programOptions.filter((option) =>
          houseOrganizations.some((org) => org === option.organization_id)
        )

        const cleanValues = field.value.filter((program) =>
          houseOrganizations.some((org) => org === program.organization_id)
        )
        helper.setValue(cleanValues)

        setProgramOptions(newProgramOptionList)
      }

      onSetIsLoadingPrograms(false)
    } catch (error) {
      onSetIsLoadingPrograms(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houseOrganizations, getOrganizationProgramList])

  useEffect(() => {
    updateProgramListing()
  }, [updateProgramListing])

  const groupedItemTemplate = (option) => {
    return <div>{option.organization_name}</div>
  }

  return (
    <>
      <FormikMultiSelectInput
        name="programs"
        label="Programs"
        helpText="Allocate programs to this house profile"
        optionGroupLabel={"organization_name"}
        optionGroupChildren="programs"
        optionGroupTemplate={groupedItemTemplate}
        options={programOptions}
        optionLabel={"program_id_name"}
        disabled={isSubmitting}
        isInitialLoad={isLoadingPrograms}
      />
    </>
  )
}
